import React from 'react';
import { CustomTable, Button } from '@quyva/shared-ui';
import { faPlay } from '@fortawesome/free-solid-svg-icons';

export const AgentsUpdatesTablePane = ({
    filteredRows,
    columns,
    selectedRowId,
    setSelectedRowId,
    selectedRow,
    isCompact,
    leftTableContainerRef,
    leftTableContainerWidth,
    leftTableContainerHeight,
    hasUserDraggedSplitter,
    setHasUserDraggedSplitter,
    currentPage,
    setCurrentPage,
}) => {
    const TABLE_MIN_WIDTH = 980;

    if (!selectedRow) {
        return (
            <div
                ref={leftTableContainerRef}
                className='w-full h-full bg-backgrounds-white overflow-hidden'
            >
                <CustomTable
                    dataSource={filteredRows}
                    columns={columns}
                    rowKey='id'
                    tableLayout='fixed'
                    size={isCompact ? 'small' : undefined}
                    pagination={{
                        current: currentPage,
                        pageSize: 9,
                        showSizeChanger: false,
                        onChange: (page) => setCurrentPage(page),
                    }}
                    onRow={(record) => ({
                        onClick: () => setSelectedRowId(record.mergeKey || record.id),
                    })}
                    rowClassName={(record) =>
                        (record.mergeKey || record.id) === selectedRowId
                            ? 'bg-backgrounds-primary100/60'
                            : ''
                    }
                />
            </div>
        );
    }

    return (
        <div
            ref={leftTableContainerRef}
            className='w-full h-full min-w-0 overflow-x-auto overflow-y-hidden'
        >
            <CustomTable
                variant='split'
                dataSource={filteredRows}
                columns={columns}
                rowKey='id'
                size={isCompact ? 'small' : undefined}
                tableLayout='fixed'
                scroll={{ x: TABLE_MIN_WIDTH }}
                pagination={{
                    current: currentPage,
                    pageSize: 9,
                    showSizeChanger: false,
                    onChange: (page) => setCurrentPage(page),
                }}
                onRow={(record) => ({
                    onClick: () => setSelectedRowId(record.mergeKey || record.id),
                })}
                rowClassName={(record) =>
                    (record.mergeKey || record.id) === selectedRowId
                        ? 'bg-backgrounds-primary100/60'
                        : ''
                }
            />
        </div>
    );
};

// Agent runs have no Stop: once started, their tools act on outside systems, so a run
// can't be stopped part-way cleanly. The only action is resuming a suspended run.
export const buildAgentsActionRenderer = ({
    onResume,
    isResumeDisabled,
    getResumeHoverText,
    canResume,
}) => {
    return (_value, record) => {
        // 'waiting' is a suspension awaiting user input (a form or an approval); 'paused' is
        // the older generic value. Both are suspended states.
        const isSuspended = ['paused', 'waiting'].includes(
            String(record.status).toLowerCase()
        );
        // Not every suspension is an admin's to release - one waiting on the run's own user
        // belongs in their task tab. canResume decides; without it, every suspension shows
        // Resume, which is the original behaviour.
        const isPaused =
            isSuspended &&
            (typeof canResume === 'function' ? Boolean(canResume(record)) : true);
        const resumeDisabled = typeof isResumeDisabled === 'function'
            ? Boolean(isResumeDisabled(record))
            : false;
        const resumeHoverText = typeof getResumeHoverText === 'function'
            ? getResumeHoverText(record)
            : 'Resume agent execution';

        if (!isPaused) {
            return (
                <div className='w-full flex items-center justify-center'>
                    <span className='text-texts-secondary300/40 select-none'>----</span>
                </div>
            );
        }

        return (
            <div className='w-full flex items-center justify-center gap-2'>
                <Button
                    className='w-[24px] h-[24px] flex items-center justify-center'
                    type='success'
                    hoverText={resumeHoverText}
                    icon={faPlay}
                    disabled={resumeDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onResume(record);
                    }}
                />
            </div>
        );
    };
};
